import { ReactNode, createContext, useContext, useMemo } from 'react';
import axios, { AxiosInstance } from 'axios';
import { Components, Paths } from './gen/client';

// Re-export types for convenience
export type { Components, Paths };

/**
 * API Response Types - derived from OpenAPI generated types
 */
export type GetInvoicesResponse = Paths.GetInvoices.Responses.$200;
export type GetInvoiceResponse = Paths.GetInvoice.Responses.$200;
export type GetSearchCustomersResponse = Paths.GetSearchCustomers.Responses.$200;
export type GetSearchProductsResponse = Paths.GetSearchProducts.Responses.$200;
export type PostInvoicesResponse = Paths.PostInvoices.Responses.$200;
export type PutInvoiceResponse = Paths.PutInvoice.Responses.$200;

/**
 * API Request Body Types - derived from OpenAPI generated types
 */
export type InvoiceCreatePayload = Components.Schemas.InvoiceCreatePayload;
export type InvoiceUpdatePayload = Components.Schemas.InvoiceUpdatePayload;
export type InvoiceLineUpdatePayload = Components.Schemas.InvoiceLineUpdatePayload;

/**
 * Typed API Client interface
 * All methods return properly typed responses instead of `any`
 */
export interface ApiClient {
  getInvoices: (params?: { 
    page?: number; 
    per_page?: number; 
    filter?: string; 
    sort?: string;
  }) => Promise<{ data: GetInvoicesResponse }>;
  
  getInvoice: (params: { id: number }) => Promise<{ data: GetInvoiceResponse }>;
  
  postInvoices: (
    params: null, 
    body: { invoice: InvoiceCreatePayload }
  ) => Promise<{ data: PostInvoicesResponse }>;
  
  putInvoice: (
    params: { id: number }, 
    body: { invoice: InvoiceUpdatePayload }
  ) => Promise<{ data: PutInvoiceResponse }>;
  
  deleteInvoice: (params: { id: number }) => Promise<{ data: void }>;
  
  getSearchCustomers: (params?: { 
    query?: string; 
    page?: number; 
    per_page?: number;
  }) => Promise<{ data: GetSearchCustomersResponse }>;
  
  getSearchProducts: (params?: { 
    query?: string; 
    page?: number; 
    per_page?: number;
  }) => Promise<{ data: GetSearchProductsResponse }>;
}

function createApiClient(baseURL: string, token: string): ApiClient {
  const client: AxiosInstance = axios.create({
    baseURL,
    headers: {
      'X-SESSION': token,
      'Content-Type': 'application/json',
    },
  });

  return {
    getInvoices: async (params) => {
      const response = await client.get<GetInvoicesResponse>('/invoices', { params });
      return { data: response.data };
    },
    getInvoice: async (params) => {
      const response = await client.get<GetInvoiceResponse>(`/invoices/${params.id}`);
      return { data: response.data };
    },
    postInvoices: async (_params, body) => {
      const response = await client.post<PostInvoicesResponse>('/invoices', body);
      return { data: response.data };
    },
    putInvoice: async (params, body) => {
      const response = await client.put<PutInvoiceResponse>(`/invoices/${params.id}`, body);
      return { data: response.data };
    },
    deleteInvoice: async (params) => {
      await client.delete(`/invoices/${params.id}`);
      return { data: undefined as void };
    },
    getSearchCustomers: async (params) => {
      const response = await client.get<GetSearchCustomersResponse>('/customers/search', { params });
      return { data: response.data };
    },
    getSearchProducts: async (params) => {
      const response = await client.get<GetSearchProductsResponse>('/products/search', { params });
      return { data: response.data };
    },
  };
}

interface ApiContextState {
  client: ApiClient | undefined;
}

const ApiContext = createContext<ApiContextState>({ client: undefined });

interface ApiProviderProps {
  url: string;
  token: string;
  children?: ReactNode;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({ url, token, children }) => {
  const client = useMemo(() => createApiClient(url, token), [url, token]);
  return (
    <ApiContext.Provider value={{ client }}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = (): ApiClient => {
  const { client } = useContext(ApiContext);
  if (!client) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return client;
};
